import firebase from "firebase";
import firebaseApp from "../config/firebase";
import Promise from "bluebird";
import map from "lodash/map";

import { handleException } from "../app";
import { PROJECT_ACTION_TYPES } from "./constants";
import {
  inviteCollaborator,
  deleteInvitation,
  deleteCollaborator,
} from "../collaborators";
import { getActiveProject } from "./selectors";
import { deleteTimelineEvent } from "../timeline";
import { deleteTask } from "../tasks";

import type { Dispatch, GetState, Id } from "../types";
import type {
  CreateProjectParamsObject,
  UpdateProjectParamsObject,
  DeleteProjectObject,
} from "./types";
import type Project from "../../models/Project";

type UploadProjectPhotoParams = {
  projectId: string,
  uri: string,
  mimeType?: string,
};

const uploadProjectPhoto = async ({
  projectId,
  uri,
  mimeType = "image/jpeg",
}: uploadProjectPhotoParams) => {
  if (uri) {
    const storage = firebaseApp.storage();
    const ref = storage.ref().child(`projectPhotos/${projectId}`);
    const blob = new Blob([uri], { type: uri.type });
    const upload = await ref.put(blob, { contentType: uri.type });

    const url = await upload.ref.getDownloadURL();

    return url;
  } else {
    return null;
  }
};

export function createProject(
  params: CreateProjectParamsObject
): (dispatch: Dispatch, getState: GetState) => Promise<string> {
  return async (dispatch: Dispatch, getState: GetState): Promise<string> => {
    dispatch({
      type: PROJECT_ACTION_TYPES.CREATE_PROJECT,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const project = {
        name: params.name,
        date: params.date,
        createdBy: currentUser.uid,
        timezone: params.timezone,
        photoURL: params.photoURL || params.photoURI || '',
      };
      const projectRef = await db.ref("projects").push(project);
      const projectId = projectRef.key;

      await db.ref("collaborators").push({
        user: currentUser.uid,
        project: projectId,
        role: "owner",
      });

      if (params.collaboratorEmail) {
        await dispatch(inviteCollaborator(params.collaboratorEmail, projectId));
      }

      let photoURL = "";
      if (params.photoURI) {
        photoURL = await uploadProjectPhoto({
          projectId: projectId,
          uri: params.photoURI,
        });
      }

      if (photoURL.length) {
        await projectRef.child("photoURL").set(photoURL);
      }

      dispatch({
        type: PROJECT_ACTION_TYPES.RECEIVE_PROJECT,
        payload: {
          project: {
            uid: projectId,
            role: "owner",
            ...project,
            photoURL,
          },
        },
      });
      return projectId;
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function updateProject(
  params: UpdateProjectParamsObject
): (dispatch: Dispatch, getState: GetState) => Promise<string> {
  return async (dispatch: Dispatch, getState: GetState): Promise<string> => {
    dispatch({
      type: PROJECT_ACTION_TYPES.UPDATE_PROJECT,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const project = {
        name: params.name,
        photoURL: params.photoURI,
        date: params.date,
        createdBy: currentUser.uid,
        timezone: params.timezone,
      };
      await db.ref(`projects/${params.projectId}`).set(project);
      const projectRef = selectProjectWithId(params.projectId);
      const value = await db.ref(`projects/${params.projectId}`).once("value");

      if (params.photoURI != params.photoURL) {
        const photoURL = await uploadProjectPhoto({
          projectId: params.projectId,
          uri: params.photoURI,
        });

        await projectRef.child("photoURL").set(photoURL);
      }

      dispatch({
        type: PROJECT_ACTION_TYPES.RECEIVE_UPDATED_PROJECT,
        payload: {
          project: value.toJSON(),
        },
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function deleteProject(
  project: Project
): (dispatch: Dispatch, getState: GetState) => Promise<string> {
  return async (dispatch: Dispatch, getState: GetState): Promise<string> => {
    dispatch({
      type: PROJECT_ACTION_TYPES.DELETE_PROJECT,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      await db.ref(`projects/${project.uid}`).set(null);

      // delete project's timeline events (to avoid notifications)
      const timelineEventsRef = db
        .ref("timelineEvents")
        .orderByChild("projectId")
        .equalTo(project.uid);
      await timelineEventsRef.on("value", (timelineEvents) => {
        const eventsData = timelineEvents.toJSON();
        for (var timelineEvent in eventsData) {
          dispatch(deleteTimelineEvent({ uid: timelineEvent }));
        }
      });

      // delete project's unnaccepted invites (to avoid notifications)
      const invitesRef = db
        .ref("collaboratorInvites")
        .orderByChild("project")
        .equalTo(project.uid);
      await invitesRef.on("value", (invites) => {
        const invitesData = invites.toJSON();
        for (var invite in invitesData) {
          dispatch(deleteInvitation(invite));
        }
      });

      // delete project's collaborators
      const collaboratorsRef = db
        .ref("collaborators")
        .orderByChild("project")
        .equalTo(project.uid);
      await collaboratorsRef.on("value", (collaborators) => {
        const collabData = collaborators.toJSON();
        for (var collaborator in collabData) {
          dispatch(deleteCollaborator(collaborator));
        }
      });

      //TODO: Delete tasks and subTasks

      dispatch({
        type: PROJECT_ACTION_TYPES.RECEIVE_DELETED_PROJECT,
        payload: {
          project: {
            ...project,
          },
        },
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function leaveProject(
  project: Project
): (displatch: Dispatch, getState: GetState) => Promise<string> {
  return async (dispatch: Dispatch, getState: GetState): Promise<string> => {
    dispatch({
      type: PROJECT_ACTION_TYPES.DELETE_COLLABORATOR,
    });
    try {
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const db = firebaseApp.database();
      let collaboratorId = null;
      await db
        .ref("collaborators")
        .orderByChild("user")
        .equalTo(currentUser.uid)
        .once("value")
        .then((snapshot) => snapshot.val())
        .then((collabs) => {
          collaboratorId = Object.keys(collabs)
            .map((uid) =>
              collabs[uid].project === project.uid &&
              collabs[uid].role === "guest"
                ? uid
                : null
            )
            .filter((uid) => uid !== null)[0];
        });
      await db.ref("collaborators").child(collaboratorId).remove();
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}
const selectProjectWithId = (project: Id) =>
  firebaseApp.database().ref("projects").child(project);

export function loadProjects(): (
  dispatch: Dispatch,
  getState: GetState
) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: PROJECT_ACTION_TYPES.FETCH_PROJECTS,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const collaboratorsRef = db
        .ref("collaborators")
        .orderByChild("user")
        .equalTo(currentUser.uid);
      const collaborators = await collaboratorsRef.once("value");
      const collaboratorsJson = collaborators.toJSON();
      // const linkedProjectIds = map(collaboratorsJson, c => c.project);
      if (collaboratorsJson != null) {
        map(collaboratorsJson, async (collaboratorJson) => {
          const projectRef = selectProjectWithId(collaboratorJson.project);

          var i = 0;
          projectRef.on("value", (project) => {
            let projectJson = project.toJSON();
            if (projectJson !== null) {
              i += 1;
              projectJson.role = collaboratorJson.role;
              dispatch({
                type: PROJECT_ACTION_TYPES.RECEIVE_PROJECT,
                payload: {
                  project: {
                    uid: project.key,
                    ...projectJson,
                  },
                },
              });
            }
          });
        });
      } else {
        dispatch({
          type: PROJECT_ACTION_TYPES.RECEIVE_PROJECTS,
          payload: {},
        });
      }
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function setActiveProject(
  project: Project
): (Dispatch, GetState) => void {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    let activeProject = getActiveProject(state);
    if (activeProject && activeProject.id == project.id) {
      return;
    }

    const db = firebaseApp.database();
    const auth = firebaseApp.auth();
    const { currentUser } = auth;

    const collaboratorsRef = db
      .ref("collaborators")
      .orderByChild("user")
      .equalTo(currentUser.uid);
    const collaboratorsData = await collaboratorsRef.once("value");
    collaboratorsData.forEach(function (collaborator) {
      let collaboratorJSON = collaborator.val();
      if (collaboratorJSON.project === project.id) {
        project._role = collaboratorJSON.role;
      }
    });

    dispatch({
      type: PROJECT_ACTION_TYPES.RECEIVE_ACTIVE_PROJECT,
      payload: {
        project,
      },
    });
  };
}

export function clearActiveProject(): (Dispatch, GetState) => void {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const activeProject = getActiveProject(state);
    dispatch({
      type: PROJECT_ACTION_TYPES.RECEIVE_ACTIVE_PROJECT,
      payload: {},
    });
  };
}

export function toggleProjectNotifications(
  enabled: boolean
): (Dispatch, GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: PROJECT_ACTION_TYPES.TOGGLE_NOTIFICATIONS,
    });
    try {
      const db = firebaseApp.database();
      const project = getActiveProject(getState());
      // $FlowFixMe
      const notificationsRef = db.ref(
        `projects/${project.id}/notificationsEnabled`
      );
      await notificationsRef.set(enabled);
      dispatch({
        type: PROJECT_ACTION_TYPES.RECEIVE_TOGGLED_NOTIFICATIONS,
        payload: {
          enabled,
        },
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}



// WEBPACK FOOTER //
// ./src/redux/projects/actions.js